import React from 'react';
import Icon from '../../../components/AppIcon';

const SafeToSpendCalculator = ({ totalAllocated, totalSpent, remainingBudget, categories }) => {
  const daysInMonth = 28;
  const currentDay = new Date()?.getDate();
  const daysRemaining = Math.max(daysInMonth - currentDay, 1);
  
  const dailySafeLimit = remainingBudget / daysRemaining;
  const weeklySafeLimit = dailySafeLimit * 7;
  const spendingRate = totalSpent / currentDay;
  const projectedTotal = spendingRate * daysInMonth;
  const projectedOverage = Math.max(projectedTotal - totalAllocated, 0);

  const getHealthStatus = () => {
    const percentageSpent = (totalSpent / totalAllocated) * 100;
    const percentageTime = (currentDay / daysInMonth) * 100;

    if (percentageSpent > percentageTime + 10) {
      return { status: 'Over Budget', color: 'var(--color-error)', icon: 'AlertCircle' };
    } else if (percentageSpent > percentageTime) {
      return { status: 'At Risk', color: 'var(--color-warning)', icon: 'AlertTriangle' };
    } else {
      return { status: 'On Track', color: 'var(--color-success)', icon: 'CheckCircle' };
    }
  };

  const healthStatus = getHealthStatus();

  const topSpendingCategory = categories?.reduce((max, cat) => 
    cat?.spent > max?.spent ? cat : max
  , categories?.[0]);

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
          <Icon name="Calculator" size={20} color="var(--color-success)" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Safe-to-Spend Calculator</h2>
          <p className="text-sm text-muted-foreground">Real-time available funds based on current spending</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-br from-success/10 to-success/5 rounded-xl border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Daily Safe Limit</span>
            <div className="flex items-center gap-1">
              <Icon name="TrendingUp" size={14} color="var(--color-success)" />
              <span className="text-xs text-success font-medium">{daysRemaining} days left</span>
            </div>
          </div>
          <p className="text-3xl font-bold text-success mb-1">
            ${dailySafeLimit?.toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground">Based on remaining budget</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground">Weekly Limit</span>
            </div>
            <p className="text-xl font-bold text-foreground">
              ${weeklySafeLimit?.toFixed(2)}
            </p>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={16} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground">Daily Rate</span>
            </div>
            <p className="text-xl font-bold text-foreground">
              ${spendingRate?.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-foreground">Budget Health</span>
            <div className="flex items-center gap-2">
              <Icon name={healthStatus?.icon} size={16} color={healthStatus?.color} />
              <span className="text-sm font-semibold" style={{ color: healthStatus?.color }}>
                {healthStatus?.status}
              </span>
            </div>
          </div>
          <div className="relative w-full h-2 bg-background rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min((totalSpent / totalAllocated) * 100, 100)}%`,
                backgroundColor: healthStatus?.color
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">
              ${totalSpent?.toLocaleString()} spent
            </span>
            <span className="text-xs text-muted-foreground">
              ${totalAllocated?.toLocaleString()} budget
            </span>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="TrendingUp" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">Spending Projection</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Projected Total</span>
              <span className="text-sm font-semibold text-foreground">
                ${projectedTotal?.toFixed(2)}
              </span>
            </div>
            {projectedOverage > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-error">Projected Overage</span>
                <span className="text-sm font-semibold text-error">
                  +${projectedOverage?.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Lightbulb" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">Smart Recommendation</span>
          </div>
          <p className="text-sm text-foreground">
            {healthStatus?.status === 'On Track' ? `Great job! You're spending within your budget. Continue at this pace.`
              : healthStatus?.status === 'At Risk'
              ? `Consider reducing spending in ${topSpendingCategory?.name} to stay on track.`
              : `You're over budget. Reduce daily spending to $${(remainingBudget / daysRemaining)?.toFixed(2)} to recover.`
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafeToSpendCalculator;